import { useInfiniteQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Th,
} from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import {
  CoverImagesProjectResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { CoverImagesProjectStatus, SortDirection } from '@/utils/enums';

import CoverImagesTableRow from './CoverImagesTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: 'created_at',
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    vertical-align: top;
    &:nth-child(1) {
      width: calc(35% - 56px);
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 12%;
    }
    &:nth-child(3) {
      width: 12%;
    }
    &:nth-child(4) {
      width: 13%;
    }
    &:nth-child(5) {
      width: 13%;
    }
    &:nth-child(6) {
      width: 14%;
    }
    &:last-child {
      width: 56px;
      position: relative;
      padding: 16px;
    }
  }
  td {
    font-size: 12px;
    font-weight: 700;
    &:last-child {
      border-left: solid 1px ${({ theme }) => theme.colors.lightGray} !important;
    }
  }
`;

interface Props {
  selectedDateRange: {
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  };
  status: CoverImagesProjectStatus;
  specialist?: number;
  editor?: number;
}

const CoverImagesTable: FC<Props> = ({
  selectedDateRange,
  status,
  specialist,
  editor,
}) => {
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 400);

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const fetchProjects = ({
    pageParam = '/v1/cover-images/cover-images-projects',
  }) => {
    return api.get<never, PaginatedResponse<CoverImagesProjectResource>>(
      pageParam,
      {
        params: {
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
          'filter[creator_id]': specialist,
          'filter[editor_id]': editor,
          'filter[status]': status,
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
          ...(selectedDateRange.rangeStart && {
            'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
          }),
          ...(selectedDateRange.rangeEnd
            ? {
                'filter[dateTo]': selectedDateRange.rangeEnd
                  .plus({ days: 1 })
                  .toString(),
              }
            : selectedDateRange.rangeStart && {
                'filter[dateTo]': selectedDateRange.rangeStart
                  .plus({ days: 1 })
                  .toString(),
              }),
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<CoverImagesProjectResource>>(
    [
      'cover_images_projects',
      {
        sort,
        selectedDateRange,
        status,
        specialist,
        editor,
        search: debouncedSearch,
      },
    ],
    fetchProjects,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const projects = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<CoverImagesProjectResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <SearchBar value={search} setValue={setSearch} />
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Post Name</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="created_in_bp_at"
                title="Date Created"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="updated_at"
                title="Date Modified"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="creator"
                title="Specialist"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="editor"
                title="Editor"
              />
              <Th>Covers Status</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={7}
              isError={isError}
              hasData={!!projects.length}
            >
              {projects.map((project) => (
                <CoverImagesTableRow project={project} key={project.id} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default CoverImagesTable;
